/**
 * Guarded `EventSource` opener.
 *
 * React's `StrictMode` (enabled in `next.config.js` via
 * `reactStrictMode: true`) double-mounts every component in dev builds.
 * Our SSE hooks follow the standard "open in `useEffect`, close in
 * cleanup" pattern; without a guard, the first mount opens → closes →
 * reopens a connection in under a millisecond. That churn burns edge
 * connection slots, doubles `activityPollManager` subscribe/unsubscribe
 * cycles, and makes dev network panels noisy.
 *
 * This helper delays the actual `new EventSource(...)` call by a small
 * window (default 60 ms). If cleanup runs during that window — i.e.
 * we're about to be torn down by StrictMode — we bail out entirely and
 * never open the stream. The real (second) mount then opens the one
 * real connection.
 *
 * Prod behaviour is unchanged: StrictMode only double-mounts in
 * development, so the 60 ms delay is imperceptible vs a real SSE
 * handshake which already takes longer than that.
 *
 * Usage:
 *
 * ```ts
 * useEffect(() => {
 *   return openDeferredEventSource(url, (es) => {
 *     es.addEventListener('foo', handler);
 *     es.onerror = onError;
 *     esRef.current = es;  // optional, for imperative reconnect
 *   });
 * }, [url]);
 * ```
 *
 * The returned function is the cleanup — compatible with `useEffect`.
 */
export function openDeferredEventSource(
  url: string,
  configure: (es: EventSource) => void,
  delayMs: number = 60,
): () => void {
  let cancelled = false;
  let es: EventSource | null = null;

  // SSR guard — no-op if called during server rendering (shouldn't
  // happen in practice, but keeps the contract simple).
  if (typeof window === 'undefined' || typeof EventSource === 'undefined') {
    return () => {};
  }

  const timer = window.setTimeout(() => {
    if (cancelled) return;
    try {
      es = new EventSource(url);
      configure(es);
    } catch (err) {
      console.warn('[openDeferredEventSource] failed to open', url, err);
    }
  }, delayMs);

  return () => {
    cancelled = true;
    window.clearTimeout(timer);
    if (es) {
      try {
        es.close();
      } catch {
        // The stream may already be closed by an onerror handler —
        // swallow the secondary close error.
      }
      es = null;
    }
  };
}
